import React, { useMemo } from 'react'

import { POPEYE_URL } from '@core/Constants/RouteConstants'
import { FlagCountryCode, FlagIcon } from '@icontainers/design-system/dist/atoms/Icons'
import { useWidth } from '@icontainers/design-system/dist/utils'
import { useTranslation } from '@src/Application/Interfaces/Hooks/useTranslation'
import { getMaxCharacter, removeParenthesesContent } from '@src/Application/Utils/StringUtility'
import { Route } from '@src/Infrastructure/Types/Routes'

import {
    Arrow,
    Location,
    LocationName,
    LocationTooltip,
    PriceDesktop,
    PriceMobile,
    RouteContent,
    RouteLinkArrow,
    RouteLinkDesktop,
    RouteLinkMobile,
    RouteLinkWrapper,
    TypeDesktop,
    TypeMobile,
    Wrapper,
} from './RouteElement.styled'

type RouteElementType = Route & {
    className?: string
    type: string
}

export const RouteElement = (props: RouteElementType): JSX.Element => {
    const { originPort, destinationPort, lastQuote, className, type } = props
    const { t } = useTranslation()
    const { isMobile } = useWidth()
    const link = `${POPEYE_URL}/quotes/FCL/${originPort.isoCode}/PORT/${encodeURIComponent(originPort.name)}/${
        originPort.countryCode
    }/${destinationPort.isoCode}/PORT/${encodeURIComponent(destinationPort.name)}/${
        destinationPort.countryCode
    }/?dv20=1`

    const originLocation = useMemo(() => {
        return `${removeParenthesesContent(originPort.name)} (${originPort.isoCode})`
    }, [originPort.isoCode, originPort.name])

    const destinationLocation = useMemo(() => {
        return `${removeParenthesesContent(destinationPort.name)} (${destinationPort.isoCode})`
    }, [destinationPort.isoCode, destinationPort.name])

    return (
        <Wrapper className={className}>
            <RouteContent>
                <TypeDesktop color="gray" noBorder>
                    {type}
                </TypeDesktop>
                <Location>
                    <FlagIcon size={22} code={originPort.countryCode.toLocaleLowerCase() as FlagCountryCode} />
                    {isMobile ? (
                        <LocationName>{originLocation}</LocationName>
                    ) : (
                        <LocationTooltip
                            tooltipEl={() => <LocationName>{getMaxCharacter(originLocation, 25)}</LocationName>}
                        >
                            {originLocation}
                        </LocationTooltip>
                    )}
                </Location>

                <Arrow colorCode="g500" direction={isMobile ? 'down' : 'right'} />
                <Location>
                    <FlagIcon size={22} code={destinationPort.countryCode.toLocaleLowerCase() as FlagCountryCode} />
                    {isMobile ? (
                        <LocationName>{destinationLocation}</LocationName>
                    ) : (
                        <LocationTooltip
                            tooltipEl={() => <LocationName>{getMaxCharacter(destinationLocation, 25)}</LocationName>}
                        >
                            {destinationLocation}
                        </LocationTooltip>
                    )}
                </Location>
            </RouteContent>
            <PriceDesktop>
                {lastQuote && lastQuote.price.amount > 0 && (
                    <>
                        {t('from')}
                        <span>
                            {lastQuote.price.currency} {lastQuote.price.amount.toFixed(0)}
                        </span>
                    </>
                )}
            </PriceDesktop>
            <RouteLinkWrapper>
                <TypeMobile color="gray" noBorder>
                    {type}
                </TypeMobile>
                {lastQuote && lastQuote.price.amount > 0 && (
                    <PriceMobile>
                        {t('from')}
                        <span>
                            {lastQuote.price.currency} {lastQuote.price.amount.toFixed(0)}
                        </span>
                    </PriceMobile>
                )}
                <RouteLinkDesktop href={link}>
                    <RouteLinkArrow />
                </RouteLinkDesktop>
                <RouteLinkMobile to={link}>{t('viewRates')}</RouteLinkMobile>
            </RouteLinkWrapper>
        </Wrapper>
    )
}
